import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as StopCircleIcon,
  HelpOutline as UnknownIcon,
  Edit as EditIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

const SERVICE_ORDER = ['grafana', 'prometheus', 'loki', 'promtail'];
const CUSTOM_URLS_KEY = 'monitoring_custom_urls';

function StatusChip({ status }) {
  if (status === 'running') {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="실행 중"
        color="success"
        size="small"
      />
    );
  }
  if (status === 'stopped') {
    return (
      <Chip
        icon={<StopCircleIcon />}
        label="중지됨"
        color="default"
        size="small"
      />
    );
  }
  if (status === 'not_found') {
    return (
      <Chip
        label="미배포"
        color="warning"
        size="small"
      />
    );
  }
  if (status === 'unreachable') {
    return (
      <Chip
        icon={<StopCircleIcon />}
        label="접근 불가"
        color="error"
        size="small"
      />
    );
  }
  return (
    <Chip
      icon={<UnknownIcon />}
      label="알 수 없음"
      color="default"
      size="small"
    />
  );
}

function MonitoringPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [togglingService, setTogglingService] = useState(null);
  const [customUrls, setCustomUrls] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CUSTOM_URLS_KEY) || '{}');
    } catch {
      return {};
    }
  });
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlDialogService, setUrlDialogService] = useState(null);
  const [urlInput, setUrlInput] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const response = await apiService.getMonitoringStatus();
      setServices(response.data.services || {});
    } catch (error) {
      enqueueSnackbar('모니터링 상태 조회 실패', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleToggle = async (serviceId, currentlyRunning) => {
    setTogglingService(serviceId);
    try {
      if (currentlyRunning) {
        await apiService.stopMonitoringService(serviceId);
        enqueueSnackbar(`${services[serviceId]?.name} 중지 요청 완료`, { variant: 'success' });
      } else {
        await apiService.startMonitoringService(serviceId);
        enqueueSnackbar(`${services[serviceId]?.name} 시작 요청 완료`, { variant: 'success' });
      }
      // 잠시 후 상태 갱신
      setTimeout(() => loadStatus(), 2000);
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.detail || '서비스 제어 실패',
        { variant: 'error' }
      );
    } finally {
      setTogglingService(null);
    }
  };

  const getDefaultUrl = (port) => {
    const { protocol, hostname } = window.location;
    return `${protocol}//${hostname}:${port}`;
  };

  const getServiceUrl = (serviceId, port) => {
    return customUrls[serviceId] || getDefaultUrl(port);
  };

  const handleOpenLink = (serviceId, port) => {
    window.open(getServiceUrl(serviceId, port), '_blank', 'noopener,noreferrer');
  };

  const openUrlDialog = (serviceId, port) => {
    setUrlDialogService(serviceId);
    setUrlInput(customUrls[serviceId] || getDefaultUrl(port));
    setUrlDialogOpen(true);
  };

  const saveCustomUrl = () => {
    const trimmed = urlInput.trim();
    const updated = { ...customUrls };
    if (trimmed) {
      updated[urlDialogService] = trimmed;
    } else {
      delete updated[urlDialogService];
    }
    setCustomUrls(updated);
    localStorage.setItem(CUSTOM_URLS_KEY, JSON.stringify(updated));
    setUrlDialogOpen(false);
    enqueueSnackbar('접속 URL이 저장되었습니다', { variant: 'success' });
  };

  const clearCustomUrl = (serviceId) => {
    const updated = { ...customUrls };
    delete updated[serviceId];
    setCustomUrls(updated);
    localStorage.setItem(CUSTOM_URLS_KEY, JSON.stringify(updated));
    enqueueSnackbar('기본 URL로 초기화되었습니다', { variant: 'info' });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const noServicesFound = Object.values(services).every(s => s.status === 'not_found' || s.status === 'unknown');

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          모니터링 관리
        </Typography>
        <IconButton onClick={() => { setLoading(true); loadStatus(); }} title="새로고침">
          <RefreshIcon />
        </IconButton>
      </Box>

      {noServicesFound && (
        <Alert severity="info" sx={{ mb: 3 }}>
          모니터링 서비스 컨테이너를 찾을 수 없습니다. 모니터링 스택이 배포되어 있는지 확인하세요.
          배포 패키지의 <strong>docker-compose.yml</strong>에 모니터링 서비스가 포함되어 있어야 합니다.
        </Alert>
      )}

      <Grid container spacing={3}>
        {SERVICE_ORDER.map((serviceId) => {
          const service = services[serviceId];
          if (!service) return null;

          const isRunning = service.running;
          const isToggling = togglingService === serviceId;
          const canControl = service.status !== 'not_found';

          return (
            <Grid item xs={12} md={6} key={serviceId}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: isRunning ? 'success.main' : undefined,
                  opacity: service.status === 'not_found' ? 0.7 : 1,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="div">
                      {service.name}
                    </Typography>
                    <StatusChip status={service.status} />
                  </Box>

                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {service.description}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <Box mt={1}>
                    <Typography variant="body2">
                      <strong>접속 포트:</strong> {service.port}
                    </Typography>
                    {service.default_credentials && (
                      <Typography variant="body2">
                        <strong>기본 계정:</strong> {service.default_credentials}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      <strong>컨테이너:</strong> {service.container_name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                        <strong>접속 URL:</strong>{' '}
                        <span style={{ color: customUrls[serviceId] ? 'inherit' : '#888' }}>
                          {getServiceUrl(serviceId, service.port)}
                        </span>
                      </Typography>
                      <Tooltip title="접속 URL 변경">
                        <IconButton size="small" onClick={() => openUrlDialog(serviceId, service.port)}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      {customUrls[serviceId] && (
                        <Tooltip title="기본 URL로 초기화">
                          <IconButton size="small" onClick={() => clearCustomUrl(serviceId)}>
                            <LinkOffIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box display="flex" gap={1}>
                    <Tooltip title={isRunning ? '서비스 중지' : '서비스 시작'}>
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          color={isRunning ? 'error' : 'success'}
                          startIcon={
                            isToggling
                              ? <CircularProgress size={14} />
                              : isRunning
                              ? <StopIcon />
                              : <StartIcon />
                          }
                          onClick={() => handleToggle(serviceId, isRunning)}
                          disabled={isToggling || !canControl}
                        >
                          {isRunning ? '중지' : '시작'}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  <Tooltip title={isRunning ? `${service.name} 열기` : '서비스가 실행 중이지 않습니다'}>
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                        onClick={() => handleOpenLink(serviceId, service.port)}
                        disabled={!isRunning}
                      >
                        열기
                      </Button>
                    </span>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box mt={4}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>참고:</strong> 모니터링 서비스는 Docker Compose 배포 패키지에 포함된 컨테이너입니다.
            시작/중지 기능은 호스트의 Docker 명령을 통해 제어됩니다.
            서비스가 <em>미배포</em> 상태인 경우, 배포 패키지에서 <code>docker-compose up -d</code>를 실행하세요.
            nginx 리버스 프록시를 사용하는 경우, 각 서비스 카드의 수정 아이콘을 클릭하여 접속 URL을 변경할 수 있습니다.
          </Typography>
        </Alert>
      </Box>

      <Dialog open={urlDialogOpen} onClose={() => setUrlDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          접속 URL 변경 — {urlDialogService && services[urlDialogService]?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            nginx 리버스 프록시의 서브패스를 사용하는 경우 해당 URL을 입력하세요.
            비워두면 기본 포트 기반 URL이 사용됩니다.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="접속 URL"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder={urlDialogService ? getDefaultUrl(services[urlDialogService]?.port) : ''}
            helperText="예: https://example.com/grafana 또는 http://192.168.1.10:3000"
            onKeyDown={(e) => { if (e.key === 'Enter') saveCustomUrl(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUrlDialogOpen(false)}>취소</Button>
          <Button onClick={saveCustomUrl} variant="contained">저장</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MonitoringPage;
